// Container Trader load board: shared browser-side helpers.
// Option lists must match server/loadBoard/validation.js (a test checks this).

export const SIZE_OPTIONS = [
  { value: '10', label: "10'" },
  { value: '20', label: "20'" },
  { value: '40', label: "40'" },
  { value: '45', label: "45'" },
  { value: '53', label: "53'" },
];

export const TYPE_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'high-cube', label: 'High cube' },
  { value: 'open-side', label: 'Open side' },
  { value: 'double-door', label: 'Double door' },
  { value: 'refrigerated', label: 'Refrigerated' },
  { value: 'flat-rack', label: 'Flat rack' },
  { value: 'other', label: 'Other' },
];

export const EQUIPMENT_OPTIONS = [
  { value: 'tilt-deck', label: 'Tilt deck' },
  { value: 'rollback', label: 'Rollback' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'step-deck', label: 'Step deck' },
  { value: 'chassis', label: 'Chassis' },
  { value: 'any', label: 'Any equipment' },
];

export const US_STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(
  ' '
);

export const LOAD_STATUS_LABELS = {
  open: 'Open',
  covered: 'Covered',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const REQUEST_STATUS_LABELS = {
  pending: 'Waiting for the dealer',
  accepted: 'Accepted',
  declined: 'Not selected',
  withdrawn: 'Withdrawn',
};

export const CARRIER_STATUS_LABELS = {
  pending: 'Pending review',
  approved: 'Approved',
  declined: 'Declined',
  blocked: 'Blocked',
};

const labelFrom = (options, value) => (options.find(o => o.value === value) || {}).label || value;
export const sizeLabel = v => labelFrom(SIZE_OPTIONS, v);
export const typeLabel = v => labelFrom(TYPE_OPTIONS, v);
export const equipmentLabel = v => labelFrom(EQUIPMENT_OPTIONS, v);

export const formatCents = cents =>
  cents == null
    ? ''
    : `$${(Number(cents) / 100).toLocaleString('en-US', {
        minimumFractionDigits: Number(cents) % 100 ? 2 : 0,
        maximumFractionDigits: 2,
      })}`;

export const rateText = cents => (cents ? formatCents(cents) : 'Request a quote');

// Rate per straight-line mile, e.g. "$5.80/mi". Empty when unknown.
export const perMile = (cents, miles) =>
  cents && miles ? `$${(cents / 100 / miles).toFixed(2)}/mi` : '';

// "2026-10-25" -> "Oct 25, 2026" (no time-zone shift).
export const formatDay = v => {
  if (!v) return '';
  const [y, m, d] = `${v}`
    .slice(0, 10)
    .split('-')
    .map(Number);
  if (!y || !m || !d) return '';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const placeText = p => (p ? `${p.city}, ${p.state} ${p.zip}` : '');
export const laneText = load =>
  load
    ? `${load.pickup.city}, ${load.pickup.state} → ${load.dropoff.city}, ${load.dropoff.state}`
    : '';
export const containersText = load =>
  load
    ? `${load.containerCount} × ${sizeLabel(load.containerSize)} ${typeLabel(load.containerType)}`
    : '';

// Why the board is locked, in plain words, for the locked page.
export const lockedMessage = reason => {
  switch (reason) {
    case 'signed-out':
      return 'Log in with your Carrier or Dealer Pro account to see the load board.';
    case 'carrier-not-applied':
      return 'Tell us about your trucking company to request access. Carrier access is free.';
    case 'carrier-pending':
      return 'Thanks. Your carrier access request is being reviewed. We will email you when it is approved.';
    case 'carrier-declined':
      return 'Your carrier account is not approved for the load board. Contact support@thecontainertrader.com with any questions.';
    case 'plan-inactive':
      return 'Your Dealer Pro plan is not active. Update your plan to post and view loads again.';
    case 'load-board-disabled':
      return 'The load board is not available right now.';
    case 'account-inactive':
      return 'This account cannot use the load board.';
    default:
      return 'The load board is for approved carriers (free) and Dealer Pro members.';
  }
};

// Form <-> API helpers for the post-a-load form.
export const emptyLoadForm = () => ({
  pickup: { address: '', city: '', state: '', zip: '' },
  dropoff: { address: '', city: '', state: '', zip: '' },
  containerSize: '40',
  containerType: 'standard',
  containerCount: '1',
  equipment: 'tilt-deck',
  readyDate: '',
  deliverBy: '',
  rate: '',
  contactPhone: '',
  companyName: '',
  notes: '',
});

export const loadToForm = load => ({
  pickup: { ...load.pickup },
  dropoff: { ...load.dropoff },
  containerSize: load.containerSize,
  containerType: load.containerType,
  containerCount: `${load.containerCount}`,
  equipment: load.equipment,
  readyDate: load.readyDate || '',
  deliverBy: load.deliverBy || '',
  rate: load.rateCents ? `${load.rateCents / 100}` : '',
  contactPhone: load.contact.phone || '',
  companyName: load.contact.companyName || '',
  notes: load.notes || '',
});

export const formToBody = f => ({ ...f, containerCount: Number(f.containerCount) });

// Display-only hint for menus: Dealer Pro members (ctSellerPlan "pro", ctPlanStatus active or
// unset) and accounts with the Carrier user type. The server re-checks access on every request.
export const showsLoadBoardLink = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  const md = profile.metadata || {};
  const plan = `${md.ctSellerPlan || ''}`.trim().toLowerCase();
  const status = `${md.ctPlanStatus || ''}`.trim().toLowerCase() || 'active';
  const userType = `${profile.publicData?.userType || ''}`.trim();
  return (plan === 'pro' && status === 'active') || userType === 'Carrier';
};
